import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { TaskPriority, TaskStatus, TaskUser } from '../../services/taskService';
import { tokens, personColor } from '../../theme';
import { StatusPill, PersonAvatar, toneColors, PillTone } from '../../components/ui';

// ─── Vocabulary (single source of truth for the module's Polish labels) ──────

export const STATUS_ORDER: TaskStatus[] = [
  'TODO',
  'IN_PROGRESS',
  'ON_HOLD',
  'IN_REVIEW',
  'IN_APPROVAL',
  'NEEDS_REWORK',
  'DONE',
  'CANCELLED',
];

export const STATUS_LABEL: Record<TaskStatus, string> = {
  TODO: 'Do zrobienia',
  IN_PROGRESS: 'W trakcie',
  ON_HOLD: 'Wstrzymane',
  IN_REVIEW: 'Do weryfikacji',
  IN_APPROVAL: 'Do akceptacji',
  NEEDS_REWORK: 'Do poprawy',
  DONE: 'Zakończone',
  CANCELLED: 'Anulowane',
};

/** Ton etykiety statusu — te same tony co w StatusPill w calej aplikacji. */
export const STATUS_TONE: Record<TaskStatus, PillTone> = {
  TODO: 'neutral',
  IN_PROGRESS: 'plum',
  ON_HOLD: 'warning',
  IN_REVIEW: 'info',
  IN_APPROVAL: 'champagne',
  NEEDS_REWORK: 'danger',
  DONE: 'success',
  CANCELLED: 'neutral',
};

const statusColors = (status: TaskStatus) => {
  const c = toneColors(STATUS_TONE[status]);
  // Anulowane i „Do zrobienia” maja ten sam ton — kropka musi je rozrozniac (tablica, wykresy).
  return { bg: c.bg, text: c.fg, dot: status === 'CANCELLED' ? tokens.lineStrong : c.dot };
};

/** Kolory statusow wyprowadzone z tonow motywu (tlo, tekst, kropka). */
export const STATUS_COLOR: Record<TaskStatus, { bg: string; text: string; dot: string }> = STATUS_ORDER.reduce(
  (acc, s) => ({ ...acc, [s]: statusColors(s) }),
  {} as Record<TaskStatus, { bg: string; text: string; dot: string }>,
);

export const PRIORITY_ORDER: TaskPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  LOW: 'Niski',
  NORMAL: 'Normalny',
  HIGH: 'Wysoki',
  URGENT: 'Pilny',
};

/** Priorytet zwraca uwage dopiero od „Wysokiego” — niski i normalny sa spokojne. */
export const PRIORITY_TONE: Record<TaskPriority, PillTone> = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'warning',
  URGENT: 'danger',
};

/** Kolor kropki priorytetu. */
export const PRIORITY_COLOR: Record<TaskPriority, string> = {
  LOW: tokens.subtle,
  NORMAL: tokens.info,
  HIGH: tokens.warning,
  URGENT: tokens.danger,
};

export const ACTIVITY_LABEL: Record<string, string> = {
  CREATED: 'Utworzono zadanie',
  UPDATED: 'Zmieniono dane',
  ASSIGNED: 'Przypisano osobę',
  UNASSIGNED: 'Usunięto przypisanie',
  DUE_CHANGED: 'Zmieniono termin',
  STATUS_CHANGED: 'Zmieniono status',
  TIMER_STARTED: 'Uruchomiono pomiar czasu',
  TIMER_PAUSED: 'Wstrzymano pomiar czasu',
  TIMER_STOPPED: 'Zatrzymano pomiar czasu',
  TIME_CORRECTED: 'Skorygowano czas',
  CHECKLIST_CHANGED: 'Zmiana w checkliście',
  COMMENT_ADDED: 'Dodano komentarz',
  ATTACHMENT_ADDED: 'Dodano załącznik',
  ATTACHMENT_REMOVED: 'Usunięto załącznik',
  SUBMITTED_REVIEW: 'Przekazano do weryfikacji',
  SUBMITTED_APPROVAL: 'Przekazano do akceptacji',
  APPROVED: 'Zaakceptowano',
  REWORK_REQUESTED: 'Zwrócono do poprawy',
  COMPLETED: 'Zakończono zadanie',
  CANCELLED: 'Anulowano zadanie',
  ARCHIVED: 'Zarchiwizowano',
  RESTORED: 'Przywrócono z archiwum',
  DELETED: 'Usunięto zadanie',
};

export const FIELD_LABEL: Record<string, string> = {
  title: 'Tytuł',
  description: 'Opis',
  priority: 'Priorytet',
  category: 'Kategoria',
  estimatedMinutes: 'Szacowany czas',
  requiresApproval: 'Wymaga akceptacji',
  startAt: 'Data rozpoczęcia',
  dueAt: 'Termin wykonania',
  status: 'Status',
};

// ─── Formatters ─────────────────────────────────────────────────────────────

const NBSP = ' ';

export const userLabel = (u?: TaskUser | null): string => {
  if (!u) return 'Nieznany użytkownik';
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return name || u.email;
};

export const userInitials = (u?: TaskUser | null): string => {
  if (!u) return '?';
  const first = u.firstName?.[0] || u.email[0];
  const last = u.lastName?.[0] || '';
  return `${first || '?'}${last}`.toUpperCase();
};

/** Staly kolor osoby — ten sam co w PersonAvatar. */
export const avatarColor = (id: string): string => personColor(id);

/** „2 h 15 min” — ten sam zapis czasu co w reszcie aplikacji. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}${NBSP}h ${m}${NBSP}min` : `${h}${NBSP}h`;
  if (m > 0) return `${m}${NBSP}min`;
  return `${s}${NBSP}s`;
}

/** Live clock format for a running timer. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function formatMinutes(minutes?: number | null): string {
  if (minutes === null || minutes === undefined) return '—';
  return formatDuration(minutes * 60);
}

export function formatDate(value?: string | null, withTime = false): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

export function formatDateTime(value?: string | null): string {
  return formatDate(value, true);
}

/** Relative, human phrasing for deadlines ("za 3 dni", "2 dni po terminie"). */
export function formatDueLabel(dueAt?: string | null, hasTime = false): string {
  if (!dueAt) return 'Bez terminu';
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return 'Bez terminu';

  const today = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(due) - startOfDay(today)) / 86_400_000);
  const time = hasTime ? `, ${due.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}` : '';

  if (diffDays === 0) return `Dziś${time}`;
  if (diffDays === 1) return `Jutro${time}`;
  // A deadline in the past is always phrased as "po terminie" — "wczoraj" would
  // hide the fact that the task is late.
  if (diffDays === -1) return '1 dzień po terminie';
  if (diffDays > 1 && diffDays <= 7) return `Za ${diffDays} dni${time}`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} dni po terminie`;
  return formatDate(dueAt, hasTime);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / 1024 / 1024) * 10) / 10} MB`;
}

const isToday = (value: string) => {
  const d = new Date(value);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

// ─── Small shared components ────────────────────────────────────────────────

export const StatusChip: React.FC<{ status: TaskStatus; size?: 'small' | 'medium' }> = ({
  status,
  size = 'small',
}) => <StatusPill label={STATUS_LABEL[status]} tone={STATUS_TONE[status]} size={size} />;

export const PriorityChip: React.FC<{ priority: TaskPriority; compact?: boolean }> = ({
  priority,
  compact = false,
}) => (
  <Tooltip title={compact ? `Priorytet: ${PRIORITY_LABEL[priority]}` : ''}>
    <Box component="span" aria-label={`Priorytet: ${PRIORITY_LABEL[priority]}`} sx={{ display: 'inline-flex' }}>
      <StatusPill
        size="small"
        tone={PRIORITY_TONE[priority]}
        label={compact ? PRIORITY_LABEL[priority][0] : PRIORITY_LABEL[priority]}
        sx={{ '& > span:first-of-type': { bgcolor: PRIORITY_COLOR[priority] } }}
      />
    </Box>
  </Tooltip>
);

/** Kategoria zadania: spokojna etykieta z kropka w kolorze kategorii. */
export const CategoryTag: React.FC<{ name: string; color?: string | null }> = ({ name, color }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 0.9,
      py: 0.15,
      borderRadius: 999,
      bgcolor: tokens.surfaceStrong,
      color: tokens.inkSoft,
      fontSize: '0.75rem',
      fontWeight: 650,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color || tokens.subtle, flexShrink: 0 }} />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Box>
  </Box>
);

/** Termin zadania: po terminie — wyrazna czerwona etykieta, dzis — bursztyn, pozniej — zwykly tekst. */
export const DueIndicator: React.FC<{ dueAt: string | null; hasTime?: boolean; overdue?: boolean; showEmpty?: boolean }> = ({
  dueAt,
  hasTime = false,
  overdue = false,
  showEmpty = false,
}) => {
  if (!dueAt) {
    return showEmpty ? <Typography component="span" sx={{ fontSize: '0.8125rem', color: tokens.subtle }}>Bez terminu</Typography> : null;
  }
  const label = formatDueLabel(dueAt, hasTime);
  if (overdue) {
    return (
      <Tooltip title={`Termin minął: ${formatDate(dueAt, hasTime)}`}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <StatusPill
            tone="danger"
            size="small"
            noDot
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <EventBusyOutlinedIcon sx={{ fontSize: 14 }} />
                {label}
              </Box>
            )}
          />
        </Box>
      </Tooltip>
    );
  }
  const today = isToday(dueAt);
  return (
    <Tooltip title={`Termin wykonania: ${formatDate(dueAt, hasTime)}`}>
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ color: today ? tokens.warningText : tokens.muted, display: 'inline-flex' }}
      >
        <ScheduleIcon sx={{ fontSize: 16 }} />
        <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: today ? 700 : 500, color: 'inherit', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

export const AssigneeAvatars: React.FC<{ users: TaskUser[]; max?: number; size?: number }> = ({
  users,
  max = 3,
  size = 26,
}) => {
  if (users.length === 0) {
    return (
      <Typography component="span" sx={{ fontSize: '0.8125rem', color: tokens.subtle, whiteSpace: 'nowrap' }}>
        Nieprzypisane
      </Typography>
    );
  }
  const overflow = users.length > max;
  const shown = overflow ? users.slice(0, max - 1) : users;
  const rest = users.length - shown.length;
  const ring = { border: `2px solid ${tokens.paper}`, boxSizing: 'content-box' as const, fontSize: '0.75rem' };
  return (
    <Stack direction="row" alignItems="center" sx={{ pl: 0.5, flexShrink: 0 }} aria-label={`Przypisane: ${users.map(userLabel).join(', ')}`}>
      {shown.map((u) => (
        <Tooltip key={u.id} title={userLabel(u)}>
          <Box sx={{ ml: -0.5, display: 'flex' }}>
            <PersonAvatar firstName={u.firstName} lastName={u.lastName} fallback={u.email} seed={u.id} size={size} sx={ring} />
          </Box>
        </Tooltip>
      ))}
      {rest > 0 && (
        <Tooltip title={users.slice(shown.length).map(userLabel).join(', ')}>
          <Box
            sx={{
              ml: -0.5,
              width: size,
              height: size,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: tokens.surfaceStrong,
              color: tokens.inkSoft,
              fontWeight: 700,
              ...ring,
            }}
          >
            +{rest}
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

export const MetaItem: React.FC<{ icon: React.ReactNode; label: string; title?: string }> = ({
  icon,
  label,
  title,
}) => (
  <Tooltip title={title || ''}>
    <Stack direction="row" gap={0.5} alignItems="center" sx={{ color: tokens.muted }}>
      <Box sx={{ display: 'flex', '& .MuiSvgIcon-root': { fontSize: 16 } }}>{icon}</Box>
      <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
        {label}
      </Typography>
    </Stack>
  </Tooltip>
);

/** Reads a previously chosen view/filter set (per user, per browser). */
export function loadPreference<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...JSON.parse(raw) };
  } catch {
    return fallback;
  }
}

export function savePreference(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked — preferences are a convenience, not a requirement */
  }
}
